import atr from "./atr";
import { ATRStop as defaultOptions } from "./defaultOptionsForComputation";
import { isFiniteNumber } from "../utils";

const toFiniteOr = (value, fallback) =>
  isFiniteNumber(value) ? value : fallback;

export default function () {
  let options = defaultOptions;

  function calculator(data = []) {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const period = Math.max(
      1,
      Math.floor(options?.period ?? defaultOptions.period ?? 1),
    );
    const multiplier = Number(
      options?.multiplier ?? defaultOptions.multiplier ?? 1,
    );
    const useHighLow = !!(
      options?.useHighLow ??
      defaultOptions.useHighLow ??
      false
    );

    if (data.length < 2) {
      return new Array(data.length).fill(undefined);
    }

    const atrCalculator = atr().options({ windowSize: period });
    const atrValues = atrCalculator(data);

    const stops = new Array(data.length).fill(undefined);

    let prevStopValue;
    let prevTrend;

    for (let i = 1; i < data.length; i += 1) {
      const current = data[i] || {};
      const previous = data[i - 1] || {};
      const atrValue = atrValues[i];

      if (
        !isFiniteNumber(current.close) ||
        !isFiniteNumber(previous.close) ||
        !isFiniteNumber(atrValue)
      ) {
        continue;
      }

      const currentEntry = stops[i];
      let priorStop = isFiniteNumber(prevStopValue) ? prevStopValue : undefined;
      if (currentEntry) {
        if (isFiniteNumber(currentEntry.buy)) {
          priorStop = currentEntry.buy;
        } else if (isFiniteNumber(currentEntry.sell)) {
          priorStop = currentEntry.sell;
        }
      }

      if (!isFiniteNumber(priorStop)) {
        priorStop = current.close;
      }

      const baseHigh = toFiniteOr(
        useHighLow ? current.high : current.close,
        current.close,
      );
      const baseLow = toFiniteOr(
        useHighLow ? current.low : current.close,
        current.close,
      );

      const offset = atrValue * multiplier;

      let result;
      let trend;

      if (current.close > priorStop && previous.close > priorStop) {
        trend = "buy";
        result = Math.max(priorStop, baseHigh - offset);
      } else if (current.close <= priorStop && previous.close <= priorStop) {
        trend = "sell";
        result = Math.min(priorStop, baseLow + offset);
      } else if (current.close > priorStop) {
        trend = "buy";
        result = baseHigh - offset;
      } else {
        trend = "sell";
        result = baseLow + offset;
      }

      if (!isFiniteNumber(result)) {
        continue;
      }

      const nextIndex = i + 1;
      if (nextIndex >= stops.length) {
        break;
      }

      const nextEntry = stops[nextIndex] ? { ...stops[nextIndex] } : {};

      if (trend === "buy") {
        nextEntry.buy = result;
        nextEntry.sell = undefined;
      } else {
        nextEntry.sell = result;
        nextEntry.buy = undefined;
      }

      nextEntry.trend = trend;
      nextEntry.value = result;

      stops[nextIndex] = nextEntry;
      prevStopValue = result;
      prevTrend = trend;
    }

    return stops;
  }

  calculator.undefinedLength = function () {
    const period = Math.max(
      1,
      Math.floor(options?.period ?? defaultOptions.period ?? 1),
    );
    return period;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
